import { Field } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CustomFieldAccessScopeEnum,
  CustomFieldEntityEnum,
  CustomFieldLifeCyclePolicyEnum,
  CustomFieldPermissionsUiEnum,
  CustomFieldReqSchema,
  CustomFieldReqUiSchema,
  CustomFieldStatusEnum,
  CustomFieldTypeEnum,
  ICustomFieldReqSchema,
  ICustomFieldReqUiSchema,
  ICustomFieldResSchema,
} from '@monetize/types/app';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Controller, FieldPath, useForm } from 'react-hook-form';
import { MdConstruction } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  useCreateEntity,
  useGetById,
  useUpdateEntity,
} from '../../../../api/queryUtils';
import {
  MAlert,
  MBox,
  MButton,
  MCustomRadioGroup,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MIcon,
  MInput,
  MPageContainer,
  MPageLoader,
  MSettingsPageHeader,
  MText,
  MTooltip,
} from '../../../../components/Monetize';
import { MainSearchInputV2 } from '../../../../components/Monetize/MCustomSelect/components/MainSearchInputV2';
import { MCustomMultiSelect } from '../../../../components/Monetize/MCustomSelect/MCustomMultiSelect';
import { MCustomSelect } from '../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { CUSTOM_FIELDS, ROLES, ROUTES } from '../../../../constants';
import { SELECT_PLACEHOLDER } from '../../../../constants/placeholders';
import { useCustomFields } from '../../../../hooks/useCustomFields';
import { useEntitlements } from '../../../../hooks/useEntitlements';
import { useSettingsReadOnlyRule } from '../../../../hooks/useSettingsReadOnlyRule';
import { useFlags } from '../../../../services/launchDarkly';
import { logger } from '../../../../services/logger';
import { getKeyFromLabel } from '../../../../utils';
import {
  buildPermissionsConfig,
  getCustomFieldEntityDisplay,
  getUiPermissionFields,
} from '../../../../utils/customFields';
import {
  nullifyEmptyStrings,
  objectToSelectOptions,
  orderObjectsBy,
  stringifyNulls,
} from '../../../../utils/misc';
import { CustomFieldsFormDropdownValues } from './CustomFieldsFormDropdownValues';

const comingSoonTypes = [
  'Multi-line Text (Text Area)',
  'Date and Time',
  'Formula',
];
const comingSoonTypesSets = comingSoonTypes.map((key) => ({
  value: key,
  title: key,
}));

const VISIBILITY_OPTIONS = objectToSelectOptions(
  CUSTOM_FIELDS.CUSTOM_FIELD_PERMISSION_UI_DISPLAY,
);

const renderTypeItemContent = ({
  title,
  item,
  isSubtitle,
  isSelected,
  isHighlight,
  isDisabled,
}: {
  title: string;
  item: any;
  isSubtitle: boolean;
  isSelected: boolean;
  isHighlight: boolean;
  isDisabled: boolean;
}) => {
  return (
    <MBox
      display="flex"
      alignItems="center"
      w="100%"
      h="22px"
      position="relative"
      role="group"
    >
      <MText
        color="inherit"
        lineClamp={1}
        fontSize={isSubtitle ? '12px' : 'sm'}
      >
        {item.title}
      </MText>
      {isDisabled && (
        <MTooltip label="Coming soon" placement="bottom-start">
          <MBox position="relative" ml="2" display="flex" alignItems="center">
            <MIcon as={MdConstruction} color="tGray.disabledText" />
          </MBox>
        </MTooltip>
      )}
    </MBox>
  );
};

const isDropdownLikeType = (type?: string) =>
  type === CustomFieldTypeEnum.DROPDOWN ||
  type === CustomFieldTypeEnum.MULTI_SELECT;

export const CustomFieldsForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { enableMultiSelectCustomField, enableCustomFieldVisibility } =
    useFlags();
  const { usageCustomFieldsEnabled } = useEntitlements();
  const customFieldEntity = params.entity || '';
  const customFieldKey = params.key || '';

  const isEdit = !!customFieldEntity && !!customFieldKey;
  const { inputVariant } = useSettingsReadOnlyRule();

  // Usage event custom fields are only configurable when the tenant is entitled
  // to them; the entity option is otherwise hidden from the dropdown.
  const entitySelectItems = useMemo(
    () =>
      objectToSelectOptions(
        getCustomFieldEntityDisplay({
          usageCustomFieldsEnabled,
          currentEntity: customFieldEntity,
        }),
      ),
    [usageCustomFieldsEnabled, customFieldEntity],
  );

  // Only needed to warn about the usage event cap before the API rejects the
  // save; existing fields are never blocked by it.
  const { customFieldList: usageEventCustomFields } = useCustomFields(
    CustomFieldEntityEnum.USAGE_EVENT,
    true,
    !isEdit,
  );

  const {
    isLoading: isCustomFieldLoading,
    data: customField,
    isError: loadingError,
  } = useGetById<ICustomFieldResSchema>('customFields', '', {
    enabled: isEdit,
    meta: { showErrorToast: true },
    refetchOnWindowFocus: false,
    endpointArgs: { entity: customFieldEntity, key: customFieldKey },
  });

  useEffect(() => {
    if (customField) {
      setExistingFieldValues(
        new Set(customField.values?.map(({ value }) => value)),
      );
      const formData = omit(
        pick(customField, Object.keys(CustomFieldReqSchema.shape)),
        ['permissions', 'permissionsConfig'],
      );
      reset({
        ...stringifyNulls({
          ...formData,
          values: Array.isArray(formData.values)
            ? orderObjectsBy(formData.values, ['order'])
            : [],
          lifeCyclePolicies: sortBy(formData.lifeCyclePolicies) || [],
        }),
        ...getUiPermissionFields(customField.permissionsConfig),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customField]);

  useEffect(() => {
    if (loadingError) {
      navigate(ROUTES.CUSTOM_FIELDS_LIST);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingError]);

  const [existingFieldValues, setExistingFieldValues] = useState(
    () => new Set(customField?.values?.map(({ value }) => value)),
  );

  const onSuccess = () => {
    navigate(-1);
  };

  const onError = (err: unknown) => {
    logger.error(err);
    handleApiErrorToast(err);
  };

  const mutateUpdateCustomField = useUpdateEntity<
    ICustomFieldResSchema,
    ICustomFieldReqSchema
  >('customFields', { onSuccess, onError, endpointArgs: customField });

  const mutateCreateCustomField = useCreateEntity<
    ICustomFieldResSchema,
    ICustomFieldReqSchema
  >('customFields', { onSuccess, onError });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    setError,
    clearErrors,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm<ICustomFieldReqUiSchema>({
    resolver: zodResolver(CustomFieldReqUiSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: customField
      ? {
          ...stringifyNulls(
            omit(customField, [
              'permissions',
              'permissionsConfig',
              'runningUserPermissions',
            ]),
          ),
          ...getUiPermissionFields(customField.permissionsConfig),
        }
      : {
          key: '',
          description: '',
          displayLabel: '',
          status: CustomFieldStatusEnum.ACTIVE,
          type: CustomFieldTypeEnum.SINGLE_LINE_TEXT,
          values: [],
          ...getUiPermissionFields(),
        },
  });

  const onSave = (data: ICustomFieldReqUiSchema) => {
    const payloadUi = nullifyEmptyStrings({
      ...data,
      values:
        data.type !== CustomFieldTypeEnum.DROPDOWN &&
        data.type !== CustomFieldTypeEnum.MULTI_SELECT
          ? []
          : data.values
              ?.map((item, index) => ({
                ...item,
                label: item.label.trim(),
                value: item.value.trim(),
                order: index,
              }))
              .filter((item) => item.label && item.value),
    });

    payloadUi.lifeCyclePolicies =
      payloadUi.entity === CustomFieldEntityEnum.QUOTE
        ? data.lifeCyclePolicies || []
        : [];

    const parseResults = CustomFieldReqUiSchema.safeParse(payloadUi);

    if (!parseResults.success) {
      logger.warn(parseResults);
      parseResults.error.issues.forEach((issue) => {
        setError(issue.path.join('.') as FieldPath<ICustomFieldReqUiSchema>, {
          message: issue.message,
        });
      });
      return;
    }

    const payload: ICustomFieldReqSchema = {
      ...omit(parseResults.data, [
        'accessScope',
        'allRolesVisibility',
        'visibilityByRole',
      ]),
      permissionsConfig: buildPermissionsConfig(parseResults.data),
    };

    if (isEdit) {
      return mutateUpdateCustomField.mutateAsync({
        id: '',
        payload,
      });
    }
    return mutateCreateCustomField.mutateAsync(payload);
  };

  if (isCustomFieldLoading) {
    return <MPageLoader />;
  }

  const isDropdown = watch('type') === CustomFieldTypeEnum.DROPDOWN;
  const isMultiSelect = watch('type') === CustomFieldTypeEnum.MULTI_SELECT;
  const isQuoteType = watch('entity') === CustomFieldEntityEnum.QUOTE;
  const isUsageEvent = watch('entity') === CustomFieldEntityEnum.USAGE_EVENT;
  const isUsageEventLimitReached =
    !isEdit &&
    isUsageEvent &&
    usageEventCustomFields.length >=
      CUSTOM_FIELDS.USAGE_EVENT_MAX_ACTIVE_CUSTOM_FIELDS;
  const isSpecificRoles =
    watch('accessScope') === CustomFieldAccessScopeEnum.SPECIFIC_ROLES;
  const showRoleVisibility = enableCustomFieldVisibility || isSpecificRoles;

  const visibilityByRole = watch('visibilityByRole');
  const roleVisibilities = CUSTOM_FIELDS.CUSTOM_FIELD_VISIBILITY_ROLES.map(
    (role) => visibilityByRole?.[role],
  );
  const bulkVisibility = roleVisibilities.every(
    (visibility) => visibility === roleVisibilities[0],
  )
    ? roleVisibilities[0]
    : undefined;

  const handleSetAllRoles = (visibility: CustomFieldPermissionsUiEnum) => {
    CUSTOM_FIELDS.CUSTOM_FIELD_VISIBILITY_ROLES.forEach((role) => {
      setValue(
        `visibilityByRole.${role}` as FieldPath<ICustomFieldReqUiSchema>,
        visibility,
        { shouldDirty: true },
      );
    });
    void trigger('visibilityByRole');
  };

  // We are keeping the button enabled if there are errors since some custom validation errors do not clear correctly
  const isDisableSaveButton = !isDirty || !isValid || isUsageEventLimitReached;

  const customFieldTypeOptions = Object.entries(
    CUSTOM_FIELDS.CUSTOM_FIELD_TYPE_DISPLAY,
  ).reduce(
    (acc, [key, value]) => {
      // Exclude MULTI_SELECT if feature flag is disabled
      if (
        key === CustomFieldTypeEnum.MULTI_SELECT &&
        !enableMultiSelectCustomField
      ) {
        return acc;
      }
      // billing-platform only accepts a subset of types for usage events
      if (
        isUsageEvent &&
        !CUSTOM_FIELDS.USAGE_EVENT_CUSTOM_FIELD_ALLOWED_TYPES.includes(
          key as CustomFieldTypeEnum,
        )
      ) {
        return acc;
      }
      return { ...acc, [key]: value };
    },
    {} as Record<string, string>,
  );
  const typeSelectItems = isUsageEvent
    ? objectToSelectOptions(customFieldTypeOptions)
    : objectToSelectOptions(customFieldTypeOptions).concat(
        comingSoonTypesSets as any,
      );

  return (
    <MPageContainer
      alignItems="stretch"
      data-testid="custom-field-form"
      overflowX="scroll"
    >
      <MSettingsPageHeader
        divider={false}
        hasBackButton
        title={!isEdit ? 'New Custom Field' : 'Edit Custom Field'}
        id={customField?.id}
      >
        <MButton
          data-testid="cusom-field-form-submit-btn"
          variant="primary"
          isLoading={
            mutateUpdateCustomField.isPending ||
            mutateCreateCustomField.isPending
          }
          isDisabled={isDisableSaveButton}
          form="custom-field-form"
          type="submit"
        >
          Save
        </MButton>
      </MSettingsPageHeader>
      <MBox>
        <form id="custom-field-form" onSubmit={handleSubmit(onSave, onError)}>
          <MGrid maxWidth="512px" templateColumns="repeat(12, 1fr)" gap={4}>
            <MGridItem colSpan={6}>
              <MFormField
                error={errors.entity}
                label="Object"
                isRequired
                tooltip="The location where the custom field appears"
              >
                <Controller
                  name="entity"
                  control={control}
                  render={({ field }) => (
                    <MCustomSelect
                      useMainInputAsSearchInput
                      MainInputComponent={MainSearchInputV2}
                      placeholder={SELECT_PLACEHOLDER}
                      items={entitySelectItems}
                      isDisabled={isEdit}
                      {...field}
                      onChange={(nextEntity) => {
                        field.onChange(nextEntity);
                        if (
                          nextEntity === CustomFieldEntityEnum.USAGE_EVENT &&
                          !CUSTOM_FIELDS.USAGE_EVENT_CUSTOM_FIELD_ALLOWED_TYPES.includes(
                            watch('type'),
                          )
                        ) {
                          setValue(
                            'type',
                            CustomFieldTypeEnum.SINGLE_LINE_TEXT,
                            {
                              shouldDirty: true,
                              shouldValidate: true,
                            },
                          );
                          setValue('values', [], { shouldDirty: true });
                        }
                        void trigger('key');
                      }}
                    />
                  )}
                />
              </MFormField>
              {isUsageEventLimitReached && (
                <MAlert type="warning" mt={2}>
                  Usage Event already has the maximum of{' '}
                  {CUSTOM_FIELDS.USAGE_EVENT_MAX_ACTIVE_CUSTOM_FIELDS} active
                  custom fields.
                </MAlert>
              )}
            </MGridItem>
            <MGridItem colSpan={6}>
              <MFormField error={errors.status} label="Status" isRequired>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <MCustomSelect
                      placeholder={SELECT_PLACEHOLDER}
                      items={objectToSelectOptions(
                        CUSTOM_FIELDS.CUSTOM_FIELD_STATUS_DISPLAY,
                      )}
                      {...field}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>

            <MGridItem colSpan={6}>
              <MFormField
                error={errors.displayLabel}
                label="Label"
                isRequired
                tooltip="This will be the name of the field you create and will be displayed above the from"
              >
                <Controller
                  name="displayLabel"
                  control={control}
                  defaultValue=""
                  render={({ field: { onChange, ...rest } }) => (
                    <MInput
                      placeholder="Field Label"
                      onChange={(ev: ChangeEvent<HTMLInputElement>) => {
                        onChange(ev);
                        !isEdit &&
                          setValue('key', getKeyFromLabel(ev.target.value), {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                      }}
                      {...rest}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
            <MGridItem colSpan={6}>
              <MFormField
                error={errors.key}
                label="API Name"
                isRequired
                tooltip="Name in code. Used to reference this field in MonetizeNow API."
              >
                <Controller
                  name="key"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <MInput
                      placeholder="API Name"
                      isDisabled={isEdit}
                      {...field}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
            <MGridItem colSpan={12}>
              <MFormField error={errors.description} label="Description">
                <Controller
                  name="description"
                  control={control}
                  defaultValue=""
                  render={({ field: { value, ...field } }) => (
                    <MInput
                      placeholder="Internal Description"
                      value={value || ''}
                      {...field}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
            <MGridItem colSpan={12}>
              <MFormField
                error={errors.tooltip}
                label="Tooltip"
                tooltip='Additional information about the field that will displayed when the user hovers on the "i" icon'
              >
                <Controller
                  name="tooltip"
                  control={control}
                  defaultValue=""
                  render={({ field: { value, ...field } }) => (
                    <MInput
                      placeholder="Additional field info"
                      value={value || ''}
                      {...field}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
            <MGridItem colSpan={6}>
              <MFormField
                error={errors.type}
                label="Type"
                isRequired
                hint={
                  isUsageEvent
                    ? 'Usage events support Single Line Text fields only.'
                    : ''
                }
              >
                <Controller
                  name="type"
                  control={control}
                  render={({ field: { onChange, ...field } }) => (
                    <MCustomSelect
                      placeholder={SELECT_PLACEHOLDER}
                      items={typeSelectItems}
                      disabledItems={comingSoonTypes}
                      renderItemContent={renderTypeItemContent}
                      isDisabled={isEdit}
                      {...field}
                      onChange={(nextType) => {
                        onChange(nextType);
                        if (!isDropdownLikeType(nextType)) {
                          setValue('values', [], {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          clearErrors('values');
                          void trigger('values');
                        }
                      }}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
            {!showRoleVisibility && (
              <MGridItem colSpan={6}>
                <MFormField
                  error={errors.allRolesVisibility}
                  label="Visibility"
                  tooltip="Determines how this field will be displayed on records"
                  isRequired
                >
                  <Controller
                    name="allRolesVisibility"
                    control={control}
                    render={({ field }) => (
                      <MCustomSelect
                        placeholder={SELECT_PLACEHOLDER}
                        items={VISIBILITY_OPTIONS}
                        {...field}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
            )}
            {showRoleVisibility && (
              <>
                <MGridItem colSpan={12}>
                  <MFormField
                    error={errors.accessScope}
                    label="Visibility"
                    tooltip="Determines how this field will be displayed on records"
                    isRequired
                  >
                    <Controller
                      name="accessScope"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <MCustomRadioGroup
                          value={value}
                          my={1}
                          itemTitle="title"
                          itemValue="value"
                          items={
                            CUSTOM_FIELDS.CUSTOM_FIELD_ACCESS_SCOPE_OPTIONS
                          }
                          stackProps={{ direction: 'row', gap: 6 }}
                          onChange={onChange}
                        />
                      )}
                    />
                  </MFormField>
                </MGridItem>
                {!isSpecificRoles && (
                  <MGridItem colSpan={6}>
                    <MFormField
                      error={errors.allRolesVisibility}
                      label="Visibility for All Roles"
                      isRequired
                    >
                      <Controller
                        name="allRolesVisibility"
                        control={control}
                        render={({ field }) => (
                          <MCustomSelect
                            placeholder={SELECT_PLACEHOLDER}
                            items={VISIBILITY_OPTIONS}
                            {...field}
                          />
                        )}
                      />
                    </MFormField>
                  </MGridItem>
                )}
                {isSpecificRoles && (
                  <MGridItem colSpan={12}>
                    <MFormField
                      error={errors.visibilityByRole}
                      label="Visibility by Role"
                      isRequired
                    >
                      <MBox>
                        <MFlex
                          alignItems="center"
                          justifyContent="space-between"
                          bg="tGray.sidebarDark"
                          borderRadius="base"
                          px={2}
                          py={2}
                          mb={1}
                        >
                          <MText fontSize="sm" fontWeight={500}>
                            Set all roles to
                          </MText>
                          <MBox w="200px">
                            <Field.Root>
                              <MCustomSelect
                                name="setAllRolesVisibility"
                                placeholder="Mixed"
                                items={VISIBILITY_OPTIONS}
                                value={bulkVisibility ?? ''}
                                onChange={(visibility) =>
                                  handleSetAllRoles(
                                    visibility as CustomFieldPermissionsUiEnum,
                                  )
                                }
                              />
                            </Field.Root>
                          </MBox>
                        </MFlex>
                        {CUSTOM_FIELDS.CUSTOM_FIELD_VISIBILITY_ROLES.map(
                          (role) => (
                            <MFlex
                              key={role}
                              alignItems="center"
                              justifyContent="space-between"
                              px={2}
                              py={1}
                            >
                              <MText fontSize="sm">
                                {ROLES.USER_ROLES_DISPLAY[role]}
                              </MText>
                              <MBox w="200px">
                                <Controller
                                  name={
                                    `visibilityByRole.${role}` as FieldPath<ICustomFieldReqUiSchema>
                                  }
                                  control={control}
                                  render={({ field }) => (
                                    <MCustomSelect
                                      placeholder={SELECT_PLACEHOLDER}
                                      items={VISIBILITY_OPTIONS}
                                      {...field}
                                    />
                                  )}
                                />
                              </MBox>
                            </MFlex>
                          ),
                        )}
                      </MBox>
                    </MFormField>
                  </MGridItem>
                )}
              </>
            )}
            {isQuoteType && (
              <MGridItem colSpan={6}>
                <MFormField
                  error={errors.lifeCyclePolicies}
                  label="Lifecycle Policies"
                  tooltip="Determines if the value of this field will be retained on amendments and renewals, or copied from a blueprint when creating a quote from one."
                >
                  <Controller
                    name="lifeCyclePolicies"
                    control={control}
                    render={({ field }) => (
                      <MCustomMultiSelect
                        {...field}
                        value={field.value || []}
                        itemTitle="label"
                        itemValue="value"
                        items={
                          CUSTOM_FIELDS.CUSTOM_FIELDS_LIFECYCLE_POLICY_OPTIONS
                        }
                        multiple
                        variant={inputVariant}
                        closeButtonText="Apply"
                        onChange={(values) => {
                          setValue(
                            'lifeCyclePolicies',
                            values.length === 0
                              ? []
                              : sortBy(
                                  values as CustomFieldLifeCyclePolicyEnum[],
                                ),
                            {
                              shouldDirty: true,
                              shouldValidate: true,
                            },
                          );
                        }}
                        placeholder="Select policies"
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
            )}
          </MGrid>
          {(isDropdown || isMultiSelect) && (
            <MGridItem colSpan={12}>
              <DndProvider backend={HTML5Backend}>
                <CustomFieldsFormDropdownValues
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  existingFieldValues={existingFieldValues}
                />
              </DndProvider>
            </MGridItem>
          )}
        </form>
      </MBox>
    </MPageContainer>
  );
};
